/**
 * Data layer for the COMPANY panel's two clinical sections — Care Programmes
 * (GLP-1, Pause, Peptide Care, Fibromyalgia Care) and Reproductive Health
 * (Menstrual Care, Fertility Care).
 *
 * NOBODY IS NAMED ANYWHERE IN EITHER SECTION, and there is no fetcher here that
 * could name them. Most company sections have a users list and a per-person
 * page; these two deliberately have neither. Being enrolled is the disclosure:
 * a list of who is in Fertility Care is a list of who is trying to conceive,
 * and a list of who is in Fibromyalgia Care is a list of who has a chronic pain
 * condition. Both are special-category health data, and both are protected
 * characteristics an employer must not be handed a roster of.
 *
 * NO CLINICAL FIELD EVER ARRIVES. The backend reads a user id and a date from
 * each document and nothing else — no symptoms, pain scores, weight, glucose,
 * medications, diagnosis status, cycle phase, flow, sexual activity or
 * pregnancy test result is aggregated, let alone returned. That guarantee is
 * structural rather than a promise; see Backend services/careHubService.js for
 * the grep that checks it.
 *
 * SMALL COHORTS COME BACK EMPTY. Under the backend's minimum the payload is
 * `{ suppressed: true, minimum }` with no figures at all — not even the
 * enrolment count, because at this level of sensitivity the count IS the
 * disclosure. A section may report its own total while withholding a programme
 * inside it, so both the section and each row must be checked.
 *
 * TOTALS MAY BE PARTIAL. When a programme is withheld it is also excluded from
 * the section's totals, because subtracting the visible rows from a complete
 * total would recover the hidden one. `totalsAreComplete: false` is the signal
 * to label the total as covering only part of the section.
 */

import {
  getCompanyCareDashboard,
  getCompanyCareProgramme,
  getCompanyReproductiveDashboard,
  getCompanyReproductiveProgramme
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/**
 * A request that never landed. Kept distinct from both "withheld" and "nobody
 * is enrolled": an empty page would read as a confident claim that nobody uses
 * the benefit, and the withheld state would claim a privacy protection that
 * never actually ran. `GetApi` swallows network errors and returns undefined,
 * so `unwrap` throws and this is what the page gets instead.
 */
const failedShape = () => ({ error: true, suppressed: false });

const adaptProgrammeRow = (row = {}) => {
  if (row.suppressed) {
    return {
      key: row.key || null,
      label: row.label || '',
      unit: row.unit || 'check-ins',
      unitOne: row.unitOne || 'check-in',
      suppressed: true,
      minimum: row.minimum ?? 0
    };
  }

  return {
    ...row,
    suppressed: false,
    key: row.key || null,
    label: row.label || '',
    unit: row.unit || 'check-ins',
    unitOne: row.unitOne || 'check-in',
    minimum: row.minimum ?? 0,

    // Take-up
    participants: row.participants ?? 0,
    adoption: row.adoption ?? 0,
    joinedInWindow: row.joinedInWindow ?? 0,
    tenureSplit: row.tenureSplit || [],

    // Engagement over the last 30 days
    checkIns: row.checkIns ?? 0,
    activeUsers: row.activeUsers ?? 0,
    activeRate: row.activeRate ?? 0,
    dormant: row.dormant ?? 0,
    avgActiveDays: row.avgActiveDays ?? 0,
    activeDaySplit: row.activeDaySplit || [],
    weekdaySplit: row.weekdaySplit || [],
    trend: row.trend || [],

    // Null, not zero, when the earlier week had nobody to hold on to.
    retentionBase: row.retentionBase ?? 0,
    retentionRate: row.retentionRate ?? null
  };
};

export const adaptOverview = (data) => {
  if (data?.suppressed) {
    return {
      suppressed: true,
      key: data.key || null,
      label: data.label || '',
      minimum: data.minimum ?? 0,
      programmes: (data.programmes || []).map(adaptProgrammeRow)
    };
  }

  return {
    suppressed: false,
    error: false,
    key: data.key || null,
    label: data.label || '',
    minimum: data.minimum ?? 0,
    totalUsers: data.totalUsers ?? 0,

    // Distinct people on any programme in the section — no double counting.
    participants: data.participants ?? 0,
    adoption: data.adoption ?? 0,

    // Summed over reported programmes only. See the header.
    checkIns: data.checkIns ?? 0,
    activeUsers: data.activeUsers ?? 0,
    joinedInWindow: data.joinedInWindow ?? 0,
    programmesReported: data.programmesReported ?? 0,
    programmesTotal: data.programmesTotal ?? 0,
    totalsAreComplete: data.totalsAreComplete !== false,

    // Empty when any programme is withheld — the spread plus a partial total
    // would narrow the hidden programme's size.
    breadthSplit: data.breadthSplit || [],

    programmes: (data.programmes || []).map(adaptProgrammeRow)
  };
};

export const adaptProgramme = (data) => ({ ...adaptProgrammeRow(data), error: false });

const OVERVIEW_FETCHERS = {
  care: getCompanyCareDashboard,
  reproductive: getCompanyReproductiveDashboard
};

const PROGRAMME_FETCHERS = {
  care: getCompanyCareProgramme,
  reproductive: getCompanyReproductiveProgramme
};

/**
 * One section's overview. `section` is 'care' or 'reproductive' and picks the
 * endpoint prefix; the backend will not serve one section's programme through
 * the other's, so the two stay genuinely separable.
 */
export const fetchCareOverview = async (section, department = '') => {
  try {
    const call = OVERVIEW_FETCHERS[section];
    if (!call) return failedShape();
    return adaptOverview(unwrap(await call(department)));
  } catch (error) {
    console.error('fetchCareOverview error:', error);
    return failedShape();
  }
};

export const fetchCareProgramme = async (section, programme, department = '') => {
  try {
    const call = PROGRAMME_FETCHERS[section];
    if (!call) return failedShape();
    return adaptProgramme(unwrap(await call(programme, department)));
  } catch (error) {
    console.error('fetchCareProgramme error:', error);
    return failedShape();
  }
};
